import type { SyncDownstreamResponse } from "@shop-ledger/shared-types";
import { apiRequest } from "../lib/apiClient";
import { getDeviceId } from "../lib/deviceId";
import type { SyncConflictOut, SyncConflictResolveRequest } from "./types";

/** Epoch — always pulls the full conflict backlog rather than an incremental slice. This
 * app has no local sync cursor to persist (unlike `pos-pwa`'s per-tenant `since` cursor in
 * `database-client`): the "Needs Review" queue here is a point-in-time read of whatever
 * `sync_conflicts` currently holds each time the page loads/refreshes, not an incremental
 * pull into a local store. Documented in `ConflictsPage.tsx`. */
const EPOCH = "1970-01-01T00:00:00Z";

/**
 * `GET /sync/downstream` — this app only reads `conflict_outcomes` off the response (the
 * `sync_conflicts` reconciliation queue, §3.5/§9.5); `inventory_changes` is ignored since
 * this app has no local inventory projection to apply it to (that's `pos-pwa`'s job).
 *
 * **Scoping note**: `backend/app/api/v1/sync.py::sync_downstream` filters conflicts by
 * `tenant_id` only (RLS), with no `branch_id` filter at all — so a BRANCH_MANAGER calling
 * this sees the same tenant-wide conflict backlog an ACCOUNTANT/OWNER would, not a
 * branch-scoped slice. Per this app's task brief ("you don't need to reimplement scoping,
 * just call the endpoint and render what comes back"), this module does exactly that and
 * no more — flagged here rather than silently implying a branch filter exists.
 */
export async function listConflicts(accessToken: string): Promise<SyncDownstreamResponse["conflict_outcomes"]> {
  const response = await apiRequest<SyncDownstreamResponse>("/sync/downstream", {
    accessToken,
    query: { since: EPOCH, device_id: getDeviceId() },
  });
  return response.conflict_outcomes;
}

/**
 * `GET /sync/conflicts` — the known gap "Needs Review" was previously read-only is now
 * fixed (§10 of the backend doc): this is the real `SyncConflict.id`-bearing list (unlike
 * `listConflicts()` above, which reads a diff-since-timestamp view off
 * `GET /sync/downstream` that never exposed a usable id), needed to target
 * `PATCH /sync/conflicts/{id}` below. `ConflictsPage` now calls this instead.
 */
export async function listSyncConflicts(accessToken: string, resolved?: boolean): Promise<SyncConflictOut[]> {
  return apiRequest<SyncConflictOut[]>("/sync/conflicts", { accessToken, query: { resolved } });
}

/** `PATCH /sync/conflicts/{id}` — marks the conflict record itself resolved/dismissed.
 * Does NOT retroactively touch the underlying inventory/sale data — reconciling that is
 * still a separate action (e.g. a manual stock adjustment or journal entry). */
export async function resolveSyncConflict(
  accessToken: string,
  conflictId: string,
  body: SyncConflictResolveRequest,
): Promise<SyncConflictOut> {
  return apiRequest<SyncConflictOut>(`/sync/conflicts/${conflictId}`, { method: "PATCH", accessToken, body });
}
